import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Search, CheckCircle2, Clock, XCircle, DollarSign, Tent, Award, ArrowLeft, Eye, AlertCircle, Ban, History, MapPin } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending:         { icon: Clock,        color: 'text-amber-400',        bg: 'bg-amber-500/10',         label: 'Pending',          desc: 'Your application has been received and is waiting for review.' },
  under_review:    { icon: Eye,          color: 'text-blue-400',         bg: 'bg-blue-500/10',          label: 'Under Review',     desc: 'Your application is currently being reviewed by our team.' },
  payment_pending: { icon: DollarSign,   color: 'text-orange-400',       bg: 'bg-orange-500/10',        label: 'Payment Pending',  desc: 'Your application has been approved! Please complete your payment.' },
  confirmed:       { icon: CheckCircle2, color: 'text-cameroon-green',   bg: 'bg-cameroon-green/10',    label: 'Confirmed',        desc: 'Your reservation is confirmed. See you at the event!' },
  info_needed:     { icon: AlertCircle,  color: 'text-purple-400',       bg: 'bg-purple-500/10',        label: 'Info Needed',      desc: 'We need additional information from you. Please check the note below.' },
  rejected:        { icon: Ban,          color: 'text-red-400',          bg: 'bg-red-500/10',           label: 'Not Approved',     desc: 'Unfortunately, your application could not be approved at this time.' },
  cancelled:       { icon: XCircle,      color: 'text-slate-400',        bg: 'bg-slate-500/10',         label: 'Cancelled',        desc: 'This reservation has been cancelled.' },
};

const PAYMENT_LABELS = {
  pending: 'Awaiting Payment',
  awaiting_payment: 'Awaiting Payment',
  on_hold: 'On Hold',
  confirmed: 'Payment Received',
  rejected: 'N/A',
  cancelled: 'Cancelled',
};

const ReservationTrackingPage = () => {
  const { confirmationNumber } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState(confirmationNumber || '');
  const [result, setResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    setError('');
    setResult(null);
    const code = search.trim().toUpperCase();
    try {
      let res;
      try {
        res = await axios.get(`${API_URL}/tent-reservations/track/${code}`);
        setResult({ ...res.data, type: 'tent' });
      } catch {
        res = await axios.get(`${API_URL}/sponsor-applications/track/${code}`);
        setResult({ ...res.data, type: 'sponsor' });
      }
    } catch {
      setError('No reservation found with this confirmation number.');
    } finally {
      setSearching(false);
    }
  };

  React.useEffect(() => {
    if (confirmationNumber) handleSearch();
  }, [confirmationNumber]);

  const s = result ? STATUS_MAP[result.status] || STATUS_MAP.pending : null;

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar variant="dark" />
      <div className="max-w-2xl mx-auto px-4 py-20">
        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl font-bold text-white mb-2" data-testid="tracking-title">Track Your Reservation</h1>
          <p className="text-white/60">Enter your confirmation number to check your reservation or sponsorship status.</p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="CCA-R-XXXXXXXX or CCA-S-XXXXXXXX"
              className="pl-10 bg-white/5 border-white/10 text-white h-12 font-mono"
              data-testid="tracking-input"
            />
          </div>
          <Button type="submit" disabled={searching} className="bg-cameroon-green hover:bg-cameroon-green/90 h-12 px-6" data-testid="tracking-search-btn">
            {searching ? 'Searching...' : 'Search'}
          </Button>
        </form>

        {error && (
          <Card className="glass border-red-500/20">
            <CardContent className="p-6 text-center">
              <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="text-red-300">{error}</p>
            </CardContent>
          </Card>
        )}

        {result && s && (
          <Card className="glass border-white/10" data-testid="tracking-result">
            <CardContent className="p-6 space-y-5">
              {/* Status Header */}
              <div className={`p-5 rounded-lg ${s.bg} flex items-center gap-4`}>
                <s.icon className={`w-10 h-10 ${s.color}`} />
                <div className="flex-1">
                  <p className={`font-bold text-lg ${s.color}`}>{s.label}</p>
                  <p className="text-white/60 text-sm">{s.desc}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {result.type === 'tent' ? 'Tent Reservation' : 'Sponsorship Application'}
                  </p>
                </div>
                <div className="ml-auto">
                  {result.type === 'tent' ? <Tent className="w-6 h-6 text-white/30" /> : <Award className="w-6 h-6 text-white/30" />}
                </div>
              </div>

              {/* Admin Note (if present) */}
              {result.admin_note && (
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <p className="text-xs font-semibold text-purple-300 mb-1">Note from Admin</p>
                  <p className="text-white/80 text-sm">{result.admin_note}</p>
                </div>
              )}

              {/* Tent/Spot Number (if assigned) */}
              {result.tent_number && (
                <div className="p-5 rounded-lg bg-cameroon-green/10 border border-cameroon-green/30 text-center" data-testid="tent-number-display">
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <MapPin className="w-5 h-5 text-cameroon-green" />
                    <p className="text-cameroon-green text-xs font-semibold uppercase tracking-wider">Your Assigned Tent / Spot Number</p>
                  </div>
                  <p className="text-4xl font-mono font-bold text-white mt-2">{result.tent_number}</p>
                  <p className="text-white/50 text-xs mt-2">Look for this number at the event venue to locate your designated setup spot.</p>
                </div>
              )}

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <Detail label="Confirmation #" value={result.confirmation_number} mono />
                <Detail label="Event" value={result.event_title} />
                <Detail label={result.type === 'tent' ? 'Business' : 'Company'} value={result.type === 'tent' ? result.business_name : result.company_name} />
                <Detail label={result.type === 'tent' ? 'Tent Type' : 'Package'} value={result.type === 'tent' ? result.tent_type_name : result.package_name} />
                <Detail label="Amount" value={`$${result.amount?.toFixed(2)} CAD`} />
                <Detail label="Payment" value={PAYMENT_LABELS[result.payment_status] || result.payment_status} />
                <Detail label="Submitted" value={new Date(result.created_at).toLocaleDateString()} />
                {result.confirmed_at && <Detail label="Confirmed" value={new Date(result.confirmed_at).toLocaleDateString()} />}
              </div>

              {/* Status History Timeline */}
              {result.status_history && result.status_history.length > 0 && (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs font-semibold text-white/40 mb-3 flex items-center gap-1">
                    <History className="w-3 h-3" /> Status Timeline
                  </p>
                  <div className="space-y-3">
                    {result.status_history.slice().reverse().map((entry, idx) => {
                      const entryStatus = STATUS_MAP[entry.status] || STATUS_MAP.pending;
                      const EntryIcon = entryStatus.icon;
                      return (
                        <div key={`${entry.changed_at}-${idx}`} className="flex items-start gap-3">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${entryStatus.bg}`}>
                            <EntryIcon className={`w-3.5 h-3.5 ${entryStatus.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium">{entryStatus.label}</p>
                            <p className="text-xs text-white/40">
                              {new Date(entry.changed_at).toLocaleDateString()} {new Date(entry.changed_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                            {entry.note && <p className="text-xs text-white/60 mt-1 italic">{entry.note}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="text-center mt-8">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-white/40 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

const Detail = ({ label, value, mono }) => (
  <div>
    <p className="text-white/40 text-xs">{label}</p>
    <p className={`text-white text-sm font-medium ${mono ? 'font-mono' : ''}`}>{value || 'N/A'}</p>
  </div>
);

export default ReservationTrackingPage;
